import React, { useEffect, useState } from 'react';
import Alert from '@material-ui/lab/Alert';
import {
  clearAllUserSession,
  consumePendingState,
  exchangeAuthCodeForSession,
  resolvePostLoginRoute,
} from '../../auth/pcAuthBrowserFlow';
import {
  isAllowedOnCurrentHost,
  TESTPORTAL_ACCESS_DENIED_MESSAGE,
} from '../../auth/testPortalAccess';

const getLoginErrorRedirect = (errorCode) => `/Login?auth_error=${encodeURIComponent(errorCode || 'auth_failed')}`;

const PcAuthCallback = () => {
  const [statusMessage, setStatusMessage] = useState('Completing sign-in...');
  const [fatalError, setFatalError] = useState('');

  useEffect(() => {
    const run = async () => {
      // Wipe stale user data from any previous session before establishing the new one.
      // Preserve pending auth state so CSRF state validation still works.
      clearAllUserSession({ preservePendingAuthState: true });

      const query = new URLSearchParams(window.location.search);
      const providerError = query.get('error');

      if (providerError) {
        setStatusMessage('Sign-in failed. Redirecting back to login...');
        window.location.replace(getLoginErrorRedirect(providerError));
        return;
      }

      const code = (query.get('code') || '').trim();
      const state = (query.get('state') || '').trim();
      if (!code || !state) {
        setFatalError('Missing callback code/state. Please sign in again.');
        return;
      }

      const stateValidation = consumePendingState(state);
      if (!stateValidation.ok) {
        setFatalError(`State validation failed (${stateValidation.error || 'unknown'}). Please sign in again.`);
        return;
      }
      if (stateValidation.bypassed) {
        console.warn('pc-auth callback state check bypassed on localhost runtime');
      }

      const exchanged = await exchangeAuthCodeForSession(code);
      if (!exchanged) {
        setFatalError('Unable to exchange auth code. Please sign in again.');
        return;
      }

      // testportal-only allowlist: deny unauthorized identities after token exchange.
      // Prefer userinfo, then JWT claims on the access token.
      let tokenClaims = null;
      try {
        const segment = String(exchanged.accessToken || '').split('.')[1] || '';
        if (segment) {
          const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
          const normalized = padded + '='.repeat((4 - (padded.length % 4 || 4)) % 4);
          tokenClaims = JSON.parse(atob(normalized));
        }
      } catch (_) {
        tokenClaims = null;
      }
      if (!isAllowedOnCurrentHost(exchanged.user || tokenClaims || exchanged)) {
        clearAllUserSession();
        setFatalError(TESTPORTAL_ACCESS_DENIED_MESSAGE);
        return;
      }

      setStatusMessage('Sign-in complete. Redirecting...');
      try { sessionStorage.setItem('pcAuthLoginMethod', 'sso'); } catch (_) {}
      window.location.replace(resolvePostLoginRoute(stateValidation.returnTo));
    };

    run().catch((error) => {
      console.error('pc-auth callback unexpected failure', error);
      const errorDetail = (error?.message || '').trim();
      setFatalError(
        `Unexpected callback error${errorDetail ? ` (${errorDetail})` : ''}. Please sign in again.`
      );
    });
  }, []);

  if (fatalError) {
    return (
      <div style={{ margin: '2rem', maxWidth: '640px' }}>
        <Alert severity="error">{fatalError}</Alert>
      </div>
    );
  }

  return (
    <div style={{ margin: '2rem', maxWidth: '640px' }}>
      <Alert severity="info">{statusMessage}</Alert>
    </div>
  );
};

export default PcAuthCallback;
